import {
     CONST_CLIENT_BON_LIVRAISON_ETAT_EN_COURS,
     CONST_CLIENT_BON_LIVRAISON_ETAT_ENVOYE_AU_CLIENT,
     CONST_CLIENT_BON_LIVRAISON_ETAT_REDIGE,
     CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR,
} from "@common-constants/*"
import { IEditLivrableRequestModel } from "../core/_models"
import { Form, Formik, FormikHelpers } from "formik"
import { IClientBonLivraisonModel, IHttpErrorResponseModel } from "@common-models/*"
import { toast } from "react-toastify"
import { AxiosError } from "axios"
import { editBonLivrableRequest, extractHtTvaFromInvoiceRequest } from "../core/_requests"
import MyAlert from "@common-utils/MyAlert"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import React, { useCallback, useEffect, useRef, useState } from "react"
import MyDateDayMonthYearField from "@common-utils/fields/MyDateDayMonthYearField"
import MySelectField from "@common-utils/fields/MySelectField"
import MyCheckBoxField from "@common-utils/fields/MyCheckBoxField"
import MyInputField from "@common-utils/fields/MyInputField"
import * as Yup from "yup"
import Split from "react-split-it"

// ---------------------------------------------------------------------------
// Validation schema
// ---------------------------------------------------------------------------

const validationSchema = Yup.object().shape({
     dateLivraison: Yup.string().required("La date de livraison est requise"),
     etat: Yup.string().required("L'état est requis"),
     document: Yup.mixed().when("doesDocumentGoingToBeReplacedOrAdded", {
          is: true,
          then: schema => schema.required("Le document est requis"),
          otherwise: schema => schema.optional(),
     }),
})

// ---------------------------------------------------------------------------
// Dropzone — idle / scanning / done / error
// ---------------------------------------------------------------------------

const ACCEPTED_MIME = ["application/pdf", "image/jpeg", "image/jpg", "image/png"]
const ACCEPTED_LABEL = "PDF, JPEG ou PNG"

type DropzoneState = "idle" | "scanning" | "done" | "error"

interface DropzoneProps {
     onFile: (file: File) => void
     state: DropzoneState
     file: File | undefined
     onReset: () => void
}

const Dropzone = ({ onFile, state, file, onReset }: DropzoneProps) => {
     const inputRef = useRef<HTMLInputElement>(null)
     const [isDraggingOver, setIsDraggingOver] = useState(false)

     const pick = useCallback(
          (f: File) => {
               if (!ACCEPTED_MIME.includes(f.type)) {
                    toast.error(`Format non supporté. Veuillez déposer un ${ACCEPTED_LABEL}.`)
                    return
               }
               onFile(f)
          },
          [onFile]
     )

     const onDrop = useCallback(
          (e: React.DragEvent) => {
               e.preventDefault()
               setIsDraggingOver(false)
               const f = e.dataTransfer.files?.[0]
               if (f) pick(f)
          },
          [pick]
     )

     const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const f = e.target.files?.[0]
          if (f) pick(f)
          e.target.value = ""
     }

     const isInteractive = state === "idle" || state === "error"

     const borderColor = isDraggingOver ? "#0d6efd" : state === "scanning" ? "#0d6efd" : state === "done" ? "#198754" : state === "error" ? "#dc3545" : "#ced4da"

     const bgColor = isDraggingOver
          ? "rgba(13,110,253,0.06)"
          : state === "scanning"
          ? "rgba(13,110,253,0.03)"
          : state === "done"
          ? "rgba(25,135,84,0.04)"
          : state === "error"
          ? "rgba(220,53,69,0.04)"
          : "#fafafa"

     const containerStyle: React.CSSProperties = {
          position: "relative",
          border: `2px dashed ${borderColor}`,
          borderRadius: 12,
          background: bgColor,
          transition: "border-color 0.2s, background 0.2s",
          cursor: isInteractive ? "pointer" : "default",
          overflow: "hidden",
     }

     const scannerStyle: React.CSSProperties = {
          position: "absolute",
          inset: 0,
          background: "linear-gradient(180deg, transparent 0%, rgba(0,123,255,0.08) 50%, transparent 100%)",
          backgroundSize: "100% 200%",
          animation: "bl-scan 1.6s ease-in-out infinite",
          pointerEvents: "none",
          borderRadius: "inherit",
     }

     return (
          <>
               <style>{`
                    @keyframes bl-scan { 0% { background-position: 0 -100%; } 100% { background-position: 0 200%; } }
                    @keyframes bl-spin { to { transform: rotate(360deg); } }
               `}</style>
               <div
                    style={containerStyle}
                    onClick={() => isInteractive && inputRef.current?.click()}
                    onDragOver={e => {
                         e.preventDefault()
                         if (isInteractive) setIsDraggingOver(true)
                    }}
                    onDragLeave={() => setIsDraggingOver(false)}
                    onDrop={isInteractive ? onDrop : undefined}
               >
                    {state === "scanning" && <div style={scannerStyle} />}

                    <div className="d-flex flex-column align-items-center justify-content-center gap-2 py-4 px-4" style={{ minHeight: 130 }}>
                         {/* IDLE */}
                         {state === "idle" && (
                              <>
                                   <div
                                        style={{
                                             width: 44,
                                             height: 44,
                                             borderRadius: "50%",
                                             background: isDraggingOver ? "rgba(13,110,253,0.12)" : "rgba(13,110,253,0.06)",
                                             display: "flex",
                                             alignItems: "center",
                                             justifyContent: "center",
                                             transition: "background 0.2s",
                                        }}
                                   >
                                        <i className="fas fa-cloud-upload-alt text-primary" style={{ fontSize: 18 }} />
                                   </div>
                                   <div className="text-center">
                                        <p className="fw-semibold text-gray-700 mb-0" style={{ fontSize: 13 }}>
                                             {isDraggingOver ? "Déposez le fichier ici" : "Glissez-déposez le nouveau document"}
                                        </p>
                                        <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                                             ou <span className="text-primary fw-semibold">parcourez</span> · {ACCEPTED_LABEL} · max 10 Mo
                                             {" · "}les montants HT/TVA seront extraits automatiquement
                                        </p>
                                   </div>
                              </>
                         )}

                         {/* SCANNING */}
                         {state === "scanning" && (
                              <>
                                   <div
                                        style={{
                                             width: 44,
                                             height: 44,
                                             borderRadius: "50%",
                                             background: "rgba(13,110,253,0.08)",
                                             display: "flex",
                                             alignItems: "center",
                                             justifyContent: "center",
                                        }}
                                   >
                                        <i className="fas fa-search text-primary" style={{ fontSize: 18, animation: "bl-spin 2s linear infinite" }} />
                                   </div>
                                   <div className="text-center">
                                        <p className="fw-semibold text-gray-700 mb-0" style={{ fontSize: 13 }}>
                                             Extraction des montants en cours…
                                        </p>
                                        <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                                             <i className={`fas fa-file-${file?.type === "application/pdf" ? "pdf text-danger" : "image text-primary"} me-1`} />
                                             {file?.name}
                                        </p>
                                   </div>
                                   <div style={{ width: "55%", height: 3, background: "rgba(13,110,253,0.15)", borderRadius: 99, overflow: "hidden" }}>
                                        <div style={{ height: "100%", width: "40%", background: "#0d6efd", borderRadius: 99, animation: "bl-scan 1.6s ease-in-out infinite" }} />
                                   </div>
                              </>
                         )}

                         {/* DONE */}
                         {state === "done" && file && (
                              <>
                                   <div
                                        style={{
                                             width: 44,
                                             height: 44,
                                             borderRadius: "50%",
                                             background: "rgba(25,135,84,0.08)",
                                             display: "flex",
                                             alignItems: "center",
                                             justifyContent: "center",
                                        }}
                                   >
                                        <i className="fas fa-check-circle text-success" style={{ fontSize: 18 }} />
                                   </div>
                                   <div className="text-center">
                                        <p className="fw-semibold text-gray-700 mb-0" style={{ fontSize: 13 }}>
                                             Nouveau document prêt
                                        </p>
                                        <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                                             <i className={`fas fa-file-${file.type === "application/pdf" ? "pdf text-danger" : "image text-primary"} me-1`} />
                                             {file.name}
                                        </p>
                                   </div>
                                   <button
                                        type="button"
                                        className="btn btn-sm btn-light-danger py-1"
                                        onClick={e => {
                                             e.stopPropagation()
                                             onReset()
                                        }}
                                   >
                                        <i className="fas fa-times me-1" /> Supprimer
                                   </button>
                              </>
                         )}

                         {/* ERROR */}
                         {state === "error" && (
                              <>
                                   <div
                                        style={{
                                             width: 44,
                                             height: 44,
                                             borderRadius: "50%",
                                             background: "rgba(220,53,69,0.08)",
                                             display: "flex",
                                             alignItems: "center",
                                             justifyContent: "center",
                                        }}
                                   >
                                        <i className="fas fa-exclamation-triangle text-danger" style={{ fontSize: 18 }} />
                                   </div>
                                   <div className="text-center">
                                        <p className="fw-semibold text-danger mb-0" style={{ fontSize: 13 }}>
                                             Extraction échouée — montants à saisir manuellement
                                        </p>
                                        <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                                             Cliquez pour réessayer avec un autre fichier
                                        </p>
                                   </div>
                                   <button
                                        type="button"
                                        className="btn btn-sm btn-light-danger py-1"
                                        onClick={e => {
                                             e.stopPropagation()
                                             onReset()
                                        }}
                                   >
                                        <i className="fas fa-redo me-1" /> Réessayer
                                   </button>
                              </>
                         )}
                    </div>

                    <input ref={inputRef} type="file" accept={ACCEPTED_MIME.join(",")} style={{ display: "none" }} onChange={onInputChange} />
               </div>
          </>
     )
}

// ---------------------------------------------------------------------------
// Preview pane — Drive doc by default, local replacement when dropped
// ---------------------------------------------------------------------------

interface PreviewPaneProps {
     driveId: string | undefined
     replacementFile: File | undefined
}

const PreviewPane = ({ driveId, replacementFile }: PreviewPaneProps) => {
     const [objectUrl, setObjectUrl] = useState<string | null>(null)
     const [isLoading, setIsLoading] = useState(true)

     useEffect(() => {
          if (!replacementFile) {
               setObjectUrl(null)
               return
          }
          const url = URL.createObjectURL(replacementFile)
          setObjectUrl(url)
          setIsLoading(true)
          return () => URL.revokeObjectURL(url)
     }, [replacementFile])

     useEffect(() => {
          setIsLoading(true)
     }, [driveId, objectUrl])

     const isPdf = replacementFile?.type === "application/pdf"
     const driveEmbed = driveId ? `https://drive.google.com/file/d/${driveId}/preview` : null
     const src = objectUrl ?? driveEmbed
     const label = replacementFile ? replacementFile.name : "Document actuel"
     const iconClass = replacementFile ? (isPdf ? "fa-file-pdf text-danger" : "fa-file-image text-primary") : "fa-file-alt text-primary"
     const openHref = objectUrl ?? (driveId ? `https://drive.google.com/file/d/${driveId}/view` : undefined)

     if (!src)
          return (
               <div className="h-100 d-flex align-items-center justify-content-center text-muted" style={{ fontSize: 13 }}>
                    <i className="fas fa-file-slash me-2" />
                    Aucun document associé à ce livrable
               </div>
          )

     return (
          <div className="position-relative w-100" style={{ height: "82vh" }}>
               {isLoading && (
                    <div className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center bg-white top-0 start-0" style={{ zIndex: 1 }}>
                         <MySimpleSpinner size="lg" />
                    </div>
               )}
               <div className="d-flex align-items-center justify-content-between px-2 pb-2">
                    <span className="fw-semibold text-muted" style={{ fontSize: 13 }}>
                         <i className={`fas ${iconClass} me-1`} />
                         {label}
                    </span>
                    {openHref && (
                         <a href={openHref} target="_blank" rel="noreferrer" className="btn btn-sm btn-light-primary py-1 px-2" style={{ fontSize: 12 }}>
                              <i className="fas fa-external-link-alt me-1" /> Ouvrir
                         </a>
                    )}
               </div>
               {objectUrl && !isPdf ? (
                    <img
                         src={objectUrl}
                         alt="Aperçu"
                         style={{ width: "100%", height: "calc(82vh - 36px)", objectFit: "contain", background: "#f5f5f5", display: "block" }}
                         onLoad={() => setIsLoading(false)}
                    />
               ) : (
                    <iframe src={src} title="Aperçu du livrable" style={{ width: "100%", height: "calc(82vh - 36px)", border: "none" }} onLoad={() => setIsLoading(false)} />
               )}
          </div>
     )
}

// ---------------------------------------------------------------------------
// Main form
// ---------------------------------------------------------------------------

const PtfPeriodeBonCommandeLivrableEditForm = ({ bl, handleSubmitCallback = () => {} }: IPtfPeriodeBonCommandeLivrableEditFormPropsModel) => {
     const [isXlScreen, setIsXlScreen] = useState<boolean>(window.innerWidth >= 1200)
     const [selectedTabMobile, setSelectedTabMobile] = useState<"form" | "preview">("form")

     const [dropzoneState, setDropzoneState] = useState<DropzoneState>("idle")
     const [replacementFile, setReplacementFile] = useState<File | undefined>(undefined)

     useEffect(() => {
          const onResize = () => setIsXlScreen(window.innerWidth >= 1200)
          window.addEventListener("resize", onResize)
          return () => window.removeEventListener("resize", onResize)
     }, [])

     const initialValues: IEditLivrableRequestModel = {
          titre: bl.livrableIntitule,
          posteLot: bl.livrableLot,
          dateLivraison: bl.dateDebut?.format("YYYY-MM-DD"),
          etat: bl.etat,
          montantHT: bl.montantHt,
          montantTVA: bl.montantTva,
          doesDocumentGoingToBeReplacedOrAdded: false,
     }

     function handleSubmit(values: IEditLivrableRequestModel, helpers: FormikHelpers<IEditLivrableRequestModel>) {
          helpers.setStatus(null)

          editBonLivrableRequest(bl.id as number, values)
               .then(r => {
                    toast.success("Vos modifications ont bien été prises en compte.")
                    handleSubmitCallback(r.data)
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data

                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) helpers.setFieldError(key, error.errors[key])
                    }

                    helpers.setStatus(error?.detail)
                    toast.error(error?.detail, { autoClose: false })
                    helpers.setSubmitting(false)
               })
               .finally(() => helpers.setSubmitting(false))
     }

     // ── Render ────────────────────────────────────────────────────────────
     return (
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
               {helpers => {
                    function handleDropzoneFile(file: File) {
                         setReplacementFile(file)
                         setDropzoneState("scanning")
                         helpers.setFieldValue("document", file)

                         extractHtTvaFromInvoiceRequest(file)
                              .then(r => {
                                   const invoice = r.data?.invoices?.[0]
                                   if (invoice?.totalAmountHt != null) helpers.setFieldValue("montantHT", invoice.totalAmountHt)
                                   if (invoice?.totalVat != null) helpers.setFieldValue("montantTVA", invoice.totalVat)
                                   setDropzoneState("done")
                              })
                              .catch(() => {
                                   toast.error("L'extraction a échoué, veuillez saisir les montants manuellement", { autoClose: false })
                                   setDropzoneState("error")
                              })
                    }

                    function handleDropzoneReset() {
                         setReplacementFile(undefined)
                         setDropzoneState("idle")
                         helpers.setFieldValue("document", undefined)
                    }

                    function handleReplaceToggle(val: boolean) {
                         helpers.setFieldValue("doesDocumentGoingToBeReplacedOrAdded", val)
                         if (!val) handleDropzoneReset()
                    }

                    // ── Left pane: compact form ────────────────────────────
                    const leftPane = (
                         <Form noValidate autoComplete="off">
                              {helpers.status && (
                                   <MyAlert type={"danger"} classNames={"mb-3"}>
                                        {helpers.status}
                                   </MyAlert>
                              )}

                              {/* Row 1: Titre + Poste/Lot */}
                              <div className="row g-3 mb-3">
                                   <div className="col-6">
                                        <label className="form-label fw-bold fs-6">Titre du livrable</label>
                                        <MyInputField
                                             name={"titre"}
                                             value={helpers.values.titre}
                                             isInvalid={!!helpers.errors.titre}
                                             onChange={val => helpers.setFieldValue("titre", val)}
                                             placeholder={"Titre du livrable"}
                                        />
                                        {helpers.errors.titre && <div className="text-danger">{helpers.errors.titre}</div>}
                                   </div>
                                   <div className="col-6">
                                        <label className="form-label fw-bold fs-6">Poste / Lot</label>
                                        <MyInputField
                                             name={"posteLot"}
                                             value={helpers.values.posteLot}
                                             isInvalid={!!helpers.errors.posteLot}
                                             onChange={val => helpers.setFieldValue("posteLot", val)}
                                             placeholder={"Poste / Lot"}
                                        />
                                        {helpers.errors.posteLot && <div className="text-danger">{helpers.errors.posteLot}</div>}
                                   </div>
                              </div>

                              {/* Row 2: Date de livraison + État */}
                              <div className="row g-3 mb-3">
                                   <div className="col-6">
                                        <label className="form-label fw-bold fs-6 required">Date de livraison</label>
                                        <MyDateDayMonthYearField
                                             name={"dateLivraison"}
                                             value={helpers.values.dateLivraison}
                                             isInvalid={!!helpers.errors.dateLivraison}
                                             onChange={val => helpers.setFieldValue("dateLivraison", val)}
                                             placeholder={"Date de livraison"}
                                        />
                                        {helpers.errors.dateLivraison && <div className="text-danger">{helpers.errors.dateLivraison}</div>}
                                   </div>
                                   <div className="col-6">
                                        <label className="form-label fw-bold fs-6 required">État</label>
                                        <MySelectField
                                             name={"etat"}
                                             options={[
                                                  { value: CONST_CLIENT_BON_LIVRAISON_ETAT_REDIGE, label: "Non démarré" },
                                                  { value: CONST_CLIENT_BON_LIVRAISON_ETAT_EN_COURS, label: "En production" },
                                                  { value: CONST_CLIENT_BON_LIVRAISON_ETAT_ENVOYE_AU_CLIENT, label: "Livré" },
                                             ]}
                                             value={helpers.values.etat}
                                             isInvalid={!!helpers.errors.etat}
                                             onChange={val => helpers.setFieldValue("etat", val)}
                                        />
                                        {helpers.errors.etat && <div className="text-danger">{helpers.errors.etat}</div>}
                                   </div>
                              </div>

                              {/* Row 3: Montant HT + Montant TVA */}
                              <div className="row g-3 mb-3">
                                   <div className="col-6">
                                        <label className="form-label fw-bold fs-6 d-flex align-items-center gap-2">
                                             Montant HT
                                             {dropzoneState === "scanning" && <MySimpleSpinner size="sm" />}
                                        </label>
                                        <MyInputField
                                             name={"montantHT"}
                                             value={helpers.values.montantHT}
                                             type={"number"}
                                             inputMode={"numeric"}
                                             isInvalid={!!helpers.errors.montantHT}
                                             onChange={val => helpers.setFieldValue("montantHT", val)}
                                             placeholder={dropzoneState === "scanning" ? "Extraction…" : "Montant HT"}
                                        />
                                        {helpers.errors.montantHT && <div className="text-danger">{helpers.errors.montantHT}</div>}
                                   </div>
                                   <div className="col-6">
                                        <label className="form-label fw-bold fs-6 d-flex align-items-center gap-2">
                                             Montant TVA
                                             {dropzoneState === "scanning" && <MySimpleSpinner size="sm" />}
                                        </label>
                                        <MyInputField
                                             name={"montantTVA"}
                                             value={helpers.values.montantTVA}
                                             type={"number"}
                                             inputMode={"numeric"}
                                             isInvalid={!!helpers.errors.montantTVA}
                                             onChange={val => helpers.setFieldValue("montantTVA", val)}
                                             placeholder={dropzoneState === "scanning" ? "Extraction…" : "Montant TVA"}
                                        />
                                        {helpers.errors.montantTVA && <div className="text-danger">{helpers.errors.montantTVA}</div>}
                                   </div>
                              </div>

                              {/* Row 4: Replace/add document toggle + dropzone */}
                              <div className="mb-3">
                                   <div className="d-flex align-items-center gap-3 mb-3">
                                        <MyCheckBoxField
                                             name={"doesDocumentGoingToBeReplacedOrAdded"}
                                             value={helpers.values.doesDocumentGoingToBeReplacedOrAdded}
                                             isInvalid={!!helpers.errors.doesDocumentGoingToBeReplacedOrAdded}
                                             onChange={handleReplaceToggle}
                                        />
                                        <label className="form-label fw-bold fs-6 mb-0">{bl.googleDriveID ? "Remplacer le document" : "Ajouter un document"}</label>
                                   </div>

                                   {helpers.values.doesDocumentGoingToBeReplacedOrAdded && (
                                        <>
                                             <Dropzone state={dropzoneState} file={replacementFile} onFile={handleDropzoneFile} onReset={handleDropzoneReset} />
                                             {helpers.errors.document && <div className="text-danger mt-1">{helpers.errors.document as string}</div>}
                                        </>
                                   )}
                              </div>

                              {/* Submit */}
                              <div className="d-flex justify-content-end">
                                   <button
                                        className="btn btn-sm btn-primary d-inline-flex align-items-center"
                                        type="submit"
                                        disabled={helpers.isSubmitting || dropzoneState === "scanning"}
                                   >
                                        {!helpers.isSubmitting ? (
                                             <>
                                                  {" "}
                                                  Appliquer les modifications <i className="fas fa-check-circle fs-4 ms-1" />{" "}
                                             </>
                                        ) : (
                                             <MySimpleSpinner size={"sm"} />
                                        )}
                                   </button>
                              </div>
                         </Form>
                    )

                    // ── Right pane: preview ────────────────────────────────
                    const rightPane = (
                         <div className="ps-2 h-100">
                              <PreviewPane driveId={bl.googleDriveID} replacementFile={replacementFile} />
                         </div>
                    )

                    // ── Layout ─────────────────────────────────────────────
                    return (
                         <>
                              {isXlScreen ? (
                                   <Split sizes={[40, 60]} direction="horizontal" gutterSize={10}>
                                        <div className="pe-3 overflow-auto" style={{ maxHeight: "82vh" }}>
                                             {leftPane}
                                        </div>
                                        {rightPane}
                                   </Split>
                              ) : (
                                   <>
                                        <div className="d-flex justify-content-center mb-4">
                                             <div className="btn-group">
                                                  <button
                                                       className={`btn btn-sm ${selectedTabMobile === "form" ? "btn-primary" : "btn-light"}`}
                                                       onClick={() => setSelectedTabMobile("form")}
                                                  >
                                                       <i className="fas fa-edit me-2" />
                                                       Formulaire
                                                  </button>
                                                  <button
                                                       className={`btn btn-sm ${selectedTabMobile === "preview" ? "btn-primary" : "btn-light"}`}
                                                       onClick={() => setSelectedTabMobile("preview")}
                                                  >
                                                       <i className="fas fa-file-pdf me-2" />
                                                       Voir le document
                                                  </button>
                                             </div>
                                        </div>
                                        {selectedTabMobile === "form" ? leftPane : rightPane}
                                   </>
                              )}
                         </>
                    )
               }}
          </Formik>
     )
}

interface IPtfPeriodeBonCommandeLivrableEditFormPropsModel {
     bl: IClientBonLivraisonModel
     handleSubmitCallback?: (bl: IClientBonLivraisonModel) => void
}

export default PtfPeriodeBonCommandeLivrableEditForm
